'use client';

/**
 * Client-only key/value store backed by a single localStorage JSON object (STORE_NAME).
 * Product PDP writes PERSISTED_VARIANT_OPTIONS_KEY; getLastSelectedVariant reads it from the JSON blob.
 * After each successful write we fire PERSISTED_STORE_UPDATED_EVENT so same-tab listeners can refresh (the native storage event only fires in other tabs).
 */
import { createContext, useContext } from 'react';

export const STORE_NAME = 'birch-local-store';

export const PERSISTED_VARIANT_OPTIONS_KEY = 'persisted-variant-options';

export const PERSISTED_STORE_UPDATED_EVENT = 'birch-persisted-store-updated';

/**
 * For `useSyncExternalStore`: notify when persisted variant data may have changed.
 * - Same tab: `PERSISTED_STORE_UPDATED_EVENT` after writes (native `storage` does not fire in the writing tab).
 * - Other tabs: `storage` when `localStorage[STORE_NAME]` changes; `key === null` covers `localStorage.clear()`.
 */
export function subscribePersistedVariantStorage(onChange) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = (event) => {
    if (event.type === PERSISTED_STORE_UPDATED_EVENT) {
      onChange();
      return;
    }
    if (event.key === STORE_NAME || event.key === null) {
      onChange();
    }
  };

  window.addEventListener('storage', handler);
  window.addEventListener(PERSISTED_STORE_UPDATED_EVENT, handler);

  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener(PERSISTED_STORE_UPDATED_EVENT, handler);
  };
}

export const LocalStoreContext = createContext(null);

const grabStore = () => {
  try {
    const store = localStorage.getItem(STORE_NAME);

    return store ? JSON.parse(store) : {};
  } catch {
    localStorage.removeItem(STORE_NAME);

    return {};
  }
};

const persistStore = (store) => {
  try {
    localStorage.setItem(STORE_NAME, JSON.stringify(store));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PERSISTED_STORE_UPDATED_EVENT));
    }
  } catch {
    // ignore write failures (e.g. storage quota exceeded, private mode)
  }
};

const actions = {
  setItem: (key, val) => {
    const store = grabStore();
    store[key] = val;
    persistStore(store);
  },
  getItem: (key) => {
    const store = grabStore();

    return store[key];
  },
};

export function LocalStoreProvider({ children }) {
  return (
    <LocalStoreContext.Provider value={actions}>
      {children}
    </LocalStoreContext.Provider>
  );
}

export function useLocalStore() {
  const context = useContext(LocalStoreContext);

  if (!context) {
    throw new Error('useLocalStore must be used within LocalStoreProvider');
  }

  return context;
}
